import { ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { trpc, type UserSession } from "../lib/trpc";

function minutiRimasti(scadenza: Date | string) {
  const fine = typeof scadenza === "string" ? new Date(scadenza) : scadenza;
  return Math.max(0, Math.round((fine.getTime() - Date.now()) / 60_000));
}

/**
 * Accesso di assistenza in corso: si vede, sempre, dalla prima riga della pagina.
 *
 * Non e un dettaglio grafico ma il patto: un amministratore di piattaforma sta guardando
 * i dati di una azienda come se fosse il titolare, e questo deve essere scritto a schermo
 * per tutto il tempo, con la via d'uscita accanto. Il conto alla rovescia dice che la
 * sessione si chiude da sola: non e una porta lasciata aperta.
 */
export function ImpersonationBanner({ session }: { session: UserSession }) {
  const assistenza = session.impersonation;
  const [minuti, setMinuti] = useState(() => (assistenza ? minutiRimasti(assistenza.expiresAt) : 0));
  const striscia = useRef<HTMLDivElement>(null);
  const esci = trpc.platform.endImpersonation.useMutation({
    onSettled: () => window.location.assign("/console"),
  });

  useEffect(() => {
    if (!assistenza) return;
    document.body.classList.add("assistenza-attiva");
    return () => {
      document.body.classList.remove("assistenza-attiva");
      document.body.style.removeProperty("--assistenza-altezza");
    };
  }, [assistenza]);

  // L'altezza della striscia si **misura**, non si indovina: su un telefono stretto il testo
  // va a capo e una misura scritta a mano nel foglio di stile finirebbe per nascondere
  // l'intestazione dell'app dietro l'avviso. Il valore serve a chi sta sotto per farsi da parte.
  useEffect(() => {
    const elemento = striscia.current;
    if (!assistenza || !elemento) return;
    const misura = () => document.body.style.setProperty("--assistenza-altezza", `${elemento.offsetHeight}px`);
    misura();
    if (typeof ResizeObserver === "undefined") return;
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(elemento);
    return () => osservatore.disconnect();
  }, [assistenza]);

  useEffect(() => {
    if (!assistenza) return;
    const battito = window.setInterval(() => setMinuti(minutiRimasti(assistenza.expiresAt)), 30_000);
    return () => window.clearInterval(battito);
  }, [assistenza]);

  if (!assistenza) return null;

  return (
    <div className="assistenza-banner" role="status" ref={striscia}>
      <ShieldAlert size={20} aria-hidden="true" />
      <p>
        <strong>Accesso amministratore attivo</strong>
        <span>
          {assistenza.adminName} sta guardando {session.company.name} come {session.user.name}.
          {" "}Si chiude da solo fra {minuti} {minuti === 1 ? "minuto" : "minuti"}.
        </span>
      </p>
      <button
        type="button"
        className={`button button--outline assistenza-banner__esci${esci.isPending ? " button--saving" : ""}`}
        onClick={() => esci.mutate()}
      >
        {esci.isPending ? "Uscita..." : "Esci dall'assistenza"}
      </button>
    </div>
  );
}
